import { useCallback, useEffect, useState } from "react";
import { Heart, MessageCircle } from "lucide-react";
import { match } from "ts-pattern";
import { container } from "tsyringe";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { useProjectId } from "hooks/useProjectId";
import { translateISO369 } from "utils/I18nText";
import CommentService from "services/projects/CommentService";
import ForumPostLikeService from "services/projects/ForumPostLikeService";
import ManageCardMenu from "../../../components/ManageCardMenu";
import classes from "./classes.module.scss";
import PostAuthorInfo from "components/pages/ForumPost/components/PostHeader/PostAuthorInfo";

type IProps = {
	post: ForumPostResponseResource;
	onEditClick: () => void;
	onDeleteClick: () => void;
};

const commentService = container.resolve(CommentService);
const forumPostLikeService = container.resolve(ForumPostLikeService);

export default function PostCard(props: IProps) {
	const { commentsCount, likesCount } = usePostInfo(props.post);

	return (
		<div className={classes["root"]}>
			<div className={classes["header"]}>
				<div className={classes["title-container"]}>
					<Typography type="h4" weight="bold" className={classes["pre-line"]}>
						{translateISO369(props.post.title)}
					</Typography>

					<Typography type="p" weight="regular" size="medium" className={classes["pre-line"]}>
						{getDescription(translateISO369(props.post.description))}
					</Typography>
				</div>

				<ManageCardMenu onEditClick={props.onEditClick} onDeleteClick={props.onDeleteClick} className={classes["card-menu"]} />
			</div>

			<div className={classes["info-container"]}>
				<PostAuthorInfo post={props.post} />

				<div className={classes["post-info"]}>
					<div className={classes["replies-info"]}>
						<MessageCircle className={classes["bubble-icon"]} />
						<Typography type="p" size="small" weight="regular" color="primary">
							{I18n.trslt(
								match(commentsCount)
									.with(0, () => I18n.asset.projects.forum.no_answers)
									.with(1, () => I18n.asset.projects.forum.single_answer)
									.otherwise(() => I18n.asset.projects.forum.answers),
								{ n: commentsCount.toFixed() },
							)}
						</Typography>
					</div>
					<Button
						variant={EButtonVariant.OUTLINED}
						color={EButtonColor.PRIMARY}
						className={classes["like-button"]}
						startIcon={<Heart className={classes["heart-icon"]} />}>
						<Typography type="p" size="small" weight="regular" color="primary">
							{likesCount}
						</Typography>
					</Button>
				</div>
			</div>
		</div>
	);
}

const usePostInfo = (post: Pick<ForumPostResponseResource, "id">) => {
	const { projectId } = useProjectId();
	const [commentsCount, setCommentsCount] = useState<number>(0);
	const [likesCount, setLikesCount] = useState<number>(0);

	const fetchCommentsCount = useCallback(async () => {
		return commentService.count(projectId, post.id).then(({ count }) => setCommentsCount(count));
	}, [post.id, projectId]);

	const fetchLikesCount = useCallback(async () => {
		return forumPostLikeService.count(projectId, post.id).then(({ count }) => setLikesCount(count));
	}, [post.id, projectId]);

	useEffect(() => {
		fetchCommentsCount();
	}, [fetchCommentsCount]);

	useEffect(() => {
		fetchLikesCount();
	}, [fetchLikesCount]);

	return { commentsCount, likesCount };
};

function getDescription(description: string | null) {
	if (!description) return null;
	const maxLength = 120;
	if (description.length > maxLength) {
		return description.slice(0, maxLength).concat("...");
	}
	return description;
}
